import {
  Modal,
  TextField,
  Divider,
  Button,
  ScaleLogoLoading,
  SelectMenu,
  SelectMenuOption,
} from '@scale/scaleui';
import { useOCPStore } from '../hooks/useOCPStore';
import { useState, useCallback, useMemo } from 'react';
import isEmail from 'isemail';
import {
  useSignupFlowInfo,
  SignupFlowInfo,
  testFunnelWhitelist,
  useJobPostsForSignupFlowQuery,
} from '../api';
import { useAsyncFn } from 'react-use-v13';
import { JobDetailPanelData } from '@scale/shared-types';
import { TestFunnelWithNewAccount } from './TestFunnelWithNewAccount';

const signupFlowInfoToOption = (signupFlowInfo: SignupFlowInfo): SelectMenuOption<string> => ({
  id: signupFlowInfo._id,
  name: `${signupFlowInfo.name} (token: ${signupFlowInfo.token})`,
});

const jobDetailPanelDataToOption = (
  jobDetailPanelData: JobDetailPanelData,
): SelectMenuOption<string> => ({
  id: jobDetailPanelData.id,
  name: `${jobDetailPanelData.name} (ID: ${jobDetailPanelData.id})`,
});

// eslint-disable-next-line complexity
const TestFunnelModal = () => {
  const { testFunnelModalOpen, currentFlow, toggleTestFunnelModal, setInfoBannerData } =
    useOCPStore(state => ({
      testFunnelModalOpen: state.testFunnelModalOpen,
      currentFlow: state.currentFlow,
      toggleTestFunnelModal: state.toggleTestFunnelModal,
      setInfoBannerData: state.setInfoBannerData,
    }));

  const [selectedFlow, setSelectedFlow] = useState<SignupFlowInfo | null>(null);
  const { isLoading: isLoadingSignupFlowInfo, data: signupFlowInfo } = useSignupFlowInfo(
    currentFlow?._id?.startsWith('tempId_') ? undefined : currentFlow?._id,
    (data: SignupFlowInfo[]) => {
      if (data.length > 0) setSelectedFlow(data[0]);
    },
  );

  const [selectedJobPost, setSelectedJobPost] = useState<JobDetailPanelData | null>(null);
  const [activeJobPosts, setActiveJobPosts] = useState<JobDetailPanelData[]>([]);
  const { isLoading: isLoadingJobPostInfo } = useJobPostsForSignupFlowQuery(
    selectedFlow?._id,
    (data: JobDetailPanelData[]) => {
      const filteredData = data.filter(jobPost => jobPost.isActive);
      setActiveJobPosts(filteredData);
      setSelectedJobPost(filteredData?.[0] ?? null);
    },
  );

  const [userEmail, setUserEmail] = useState<string>('');
  const [promptForDeletion, setPromptForDeletion] = useState<boolean>(false);
  const [duplicateUsers, setDuplicateUsers] = useState<{ _id: string; email: string }[]>([]);
  const [selectedGetNewTestAccount, setSelectedGetNewTestAccount] = useState(false);

  const isValidEmail = useMemo(() => isEmail.validate(userEmail), [userEmail]);

  const closeModal = useCallback(() => {
    toggleTestFunnelModal(false);
    setUserEmail('');
    setPromptForDeletion(false);
  }, [setUserEmail, toggleTestFunnelModal]);

  const setSuccessBanner = useCallback(() => {
    if (!selectedFlow) {
      return;
    }

    const link = selectedJobPost
      ? `https://app.outlier.ai/en/expert/opportunities/${selectedJobPost.id}`
      : `https://app.outlier.ai/en/expert/signup?signup_flow=${selectedFlow.token}`;

    setInfoBannerData({
      cta: {
        label: 'Open Signup Link',
        onClick: () => {
          window.open(link);
          setInfoBannerData(null);
        },
      },
      buttonVariant: 'primary',
      title: `Email ${userEmail} successfully whitelisted!`,
      subtitle: 'You can now test the onboarding flow.',
      variant: 'success',
    });
  }, [setInfoBannerData, userEmail, selectedFlow]);

  const [{ loading: whitelistLoading }, whitelistUser] = useAsyncFn(
    // NOTE see https://github.com/streamich/react-use/issues/1111 for typing issue
    async (...args: any[]) => {
      const deleteExisting = args[0] as boolean;
      if (!currentFlow || !selectedFlow) {
        throw new Error(
          'Fatal error: trying to whitelist but currentFlow or selectedFlow are not set.',
        );
      }

      const result = await testFunnelWhitelist({
        onboardingFlowId: currentFlow._id,
        signupFlowId: selectedFlow._id,
        email: userEmail,
        deleteExisting,
      });

      if (result.success) {
        setSuccessBanner();
        closeModal();
        return true;
      } else {
        setDuplicateUsers(result.existingUsers ?? []);
        setPromptForDeletion(true);
        return false;
      }
    },
    [
      currentFlow,
      selectedFlow,
      userEmail,
      setSuccessBanner,
      setPromptForDeletion,
      setDuplicateUsers,
      closeModal,
    ],
  );

  const isLoading = isLoadingSignupFlowInfo;
  const noSignupFlows = !isLoading && !!signupFlowInfo && signupFlowInfo.length === 0;
  const cantContinue =
    !userEmail || !isValidEmail || noSignupFlows || whitelistLoading || isLoadingJobPostInfo;

  return (
    <Modal
      open={testFunnelModalOpen}
      className="max-w-md"
      onClose={closeModal}
      title={`Test ${currentFlow?.name ?? 'flow'}`}
    >
      {isLoading && <ScaleLogoLoading />}
      {!isLoading && (
        <>
          {noSignupFlows && (
            <div>
              <p className="leading-snug mb-4">
                No signup flows found for this onboarding flow. Please create a signup flow on the
                Signup node.
              </p>
              <Divider direction="horizontal" className="mb-4" />
              <div className="flex flex-row gap-2 justify-end items-center">
                <Button className="" onClick={closeModal} size="sm" variant="primary">
                  Close
                </Button>
              </div>
            </div>
          )}
          {!noSignupFlows && (
            <>
              {!promptForDeletion && (
                <>
                  <p className="leading-snug mb-4">
                    Select a flow and enter an email to whitelist and test the onboarding
                    experience.
                  </p>
                  <SelectMenu
                    className="mb-4"
                    label="Signup Flow"
                    options={signupFlowInfo!.map(signupFlowInfoToOption)}
                    selectedOptionId={selectedFlow?._id ?? signupFlowInfo![0]._id}
                    setSelected={(id: string) =>
                      setSelectedFlow(signupFlowInfo!.find(flow => flow._id === id)!)
                    }
                    variant="minimal"
                  />
                  {!!activeJobPosts?.length && (
                    <SelectMenu
                      className="mb-4"
                      label="Job Post"
                      options={activeJobPosts.map(jobDetailPanelDataToOption)}
                      selectedOptionId={selectedJobPost?.id ?? activeJobPosts[0].id}
                      setSelected={(id: string) =>
                        setSelectedJobPost(activeJobPosts.find(jobPost => jobPost.id === id)!)
                      }
                      variant="minimal"
                    />
                  )}
                  <TextField
                    id="user-email"
                    className="mb-4"
                    label="User Email"
                    onChange={e => setUserEmail(e.target.value)}
                    placeholder="User Email"
                    type="text"
                    leadingIcon="envelope"
                    error={!!userEmail && !isValidEmail}
                    value={userEmail}
                  />

                  {!!currentFlow?._id && signupFlowInfo && (
                    <>
                      <div className="flex flex-row items-center mb-4 px-14 gap-4">
                        <Divider direction="horizontal" />
                        <div>or</div>
                        <Divider direction="horizontal" />
                      </div>
                      <div className="mb-4">
                        Create a new test account to quickly test the steps in the flow
                      </div>
                      {!selectedGetNewTestAccount && (
                        <Button
                          onClick={() => setSelectedGetNewTestAccount(true)}
                          size="sm"
                          variant="neutral"
                        >
                          Get new test account
                        </Button>
                      )}
                      {selectedGetNewTestAccount && (
                        <TestFunnelWithNewAccount
                          signupFlowId={selectedFlow?._id ?? signupFlowInfo?.[0]?._id}
                          onCancel={() => setSelectedGetNewTestAccount(false)}
                        />
                      )}
                    </>
                  )}
                  <div className="flex flex-row gap-2 justify-end">
                    <Button className="" onClick={closeModal} size="sm" variant="neutral">
                      Cancel
                    </Button>
                    <Button
                      className=""
                      onClick={() => whitelistUser(false)}
                      size="sm"
                      variant="primary"
                      disabled={cantContinue}
                    >
                      Continue
                    </Button>
                  </div>
                </>
              )}
              {promptForDeletion && (
                <>
                  <p className="leading-snug mb-4">
                    Accounts for <b>{userEmail}</b> already exist. To continue, delete the existing
                    accounts and any information associated with each one.
                  </p>
                  <p className="leading-snug mb-4">
                    This is an irreversible action, and you should ONLY do this for test accounts
                    that you own. Do NOT do this for any actual tasker accounts.
                  </p>
                  <p className="leading-snug mb-4">
                    Deleting the accounts will let you go through the onboarding flow again.
                  </p>
                  <p className="leading-snug mb-4">
                    Existing accounts:
                    <ul>
                      {duplicateUsers.map(({ _id, email }) => (
                        <li key={_id}>
                          {email} (id:{' '}
                          <a href={`https://dashboard.scale.com/corp/lookup/${_id}`}></a>)
                        </li>
                      ))}
                    </ul>
                  </p>
                  <Divider direction="horizontal" className="mb-4" />
                  <div className="flex flex-row gap-2 justify-end">
                    <Button
                      className=""
                      onClick={() => setPromptForDeletion(false)}
                      size="sm"
                      variant="neutral"
                    >
                      Back
                    </Button>
                    <Button
                      className=""
                      onClick={() => whitelistUser(true)}
                      size="sm"
                      variant="danger"
                      disabled={cantContinue}
                    >
                      Continue and Delete Account
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default TestFunnelModal;
